'use client';

import { isHealth100Client } from '@/lib/utils/utils';
import Image from 'next/image';
import Layout from '@/components/layout/layout';
import PreferenceTile from '@/components/preference-tile/preference-tile';
import useCommPrefsQuery from '@/hooks/useCommPrefsQuery';
import { AUTOMATED_CALLS_URL, EMAILS_URL, MAIL_URL, TEXTS_URL } from '@/lib/constants';
import TextMessagingIcon from '@/assets/icons/TextMessaging.svg';
import PhoneIcon from '@/assets/icons/Phone.svg';
import EmailIcon from '@/assets/icons/Email.svg';
import MailboxIcon from '@/assets/icons/Mailbox.svg';
import styles from '@/app/communication-preferences/communication-preferences.module.scss';
import { useTranslation } from '@/components/translation';

const CommunicationPreferencesPage = () => {
  const { isFetching, isLoading, data } = useCommPrefsQuery();
  const isRxTied = data?.data?.profile?.rxTied === 'Y';
  const isEcTied = data?.data?.profile?.extraCareDetails?.extraCareTied === 'Y';
  const isEcPrimary = data?.data?.profile?.extraCareDetails?.extracareAccessLevel === 'P';

  const cmkPref = data?.data?.linkedSSKs?.filter(
    (ssk) =>
      ssk.preferencesInfo?.preferences?.[0]?.preferenceType === 'CAREMARK_PREFERENCE' &&
      ssk.preferencesInfo?.statusCode === '0000',
  );
  const isCmkTied = cmkPref?.length === 1; // mark user as untied if zero or multiple QLIDs are found

  const isSpecialtyTied = Boolean(
    data?.data?.linkedSSKs?.find(
      (prefObj) => prefObj?.preferencesInfo?.preferences?.[0]?.preferenceType === 'SPECIALTY_PREFERENCE',
    ),
  );

  // i18n translations - hooks must be called at top level
  const pageTitle = useTranslation('page.title');
  const textsLabel = useTranslation('preference.types.texts');
  const automatedCallsLabel = useTranslation('preference.types.automatedCalls');
  const emailsLabel = useTranslation('preference.types.emails');
  const emailsAndReceiptsLabel = useTranslation('preference.types.emailsAndReceipts');
  const mailLabel = useTranslation('preference.types.mail');
  const mailAndPrintLabel = useTranslation('preference.types.mailAndPrint');

  return (
    <Layout loading={isFetching || isLoading} title={pageTitle}>
      <div className={styles.preferenceTilesContainer}>
        <ul className={styles.prefTileUlBullets}>
          {/* Texts */}
          {(isRxTied || isEcPrimary || isSpecialtyTied || isCmkTied) && (
            <li>
              <PreferenceTile
                icon={<Image src={TextMessagingIcon} alt="" aria-hidden="true" />}
                label={textsLabel}
                route={TEXTS_URL}
                linkTag="account:my_account:communication_preferences:texts"
              />
            </li>
          )}

          {/* Automated calls */}
          {(isRxTied || isCmkTied) && (
            <li>
              <PreferenceTile
                icon={<Image src={PhoneIcon} alt="" aria-hidden="true" />}
                label={automatedCallsLabel}
                route={AUTOMATED_CALLS_URL}
                linkTag="account:my_account:communication_preferences:automated_calls"
              />
            </li>
          )}

          {/* Emails (and receipts) */}
          <li>
            <PreferenceTile
              icon={<Image src={EmailIcon} alt="" aria-hidden="true" />}
              label={!isHealth100Client() && isEcPrimary
                ? emailsAndReceiptsLabel
                : emailsLabel
              }
              route={EMAILS_URL}
              linkTag="account:my_account:communication_preferences:emails_and_receipts"
            />
          </li>

          {/* Mail (and print)
      Currently there is ONLY Extracare related offers under 'Mail', so showing this button only for EC tied users
       */}
          {!isHealth100Client() && isEcPrimary && (
            <li>
              <PreferenceTile
                icon={<Image src={MailboxIcon} alt="" aria-hidden="true" />}
                label={isEcTied ? mailAndPrintLabel : mailLabel}
                route={MAIL_URL}
                linkTag="account:my_account:communication_preferences:mail"
              />
            </li>
          )}
        </ul>
      </div>
    </Layout>
  );
};

export default CommunicationPreferencesPage;
